import { create } from 'zustand'
import { querystring } from "zustand-querystring";
import dayjs, { Dayjs } from 'dayjs';
import { Batch, BatchTransfer, TankInfosQuery, Site, Tank, Treshold, Water, FindBatchReportQuery, BatchInfosQuery, BatchReport, BatchReportTarget } from "types/graphql";
import { persist, createJSONStorage } from 'zustand/middleware'
import { RecursivePartial, XOR } from "types/custom"
import { indexBy } from "src/const/Helpers";
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { TANKTYPE_A, TANKTYPE_G, TANKTYPE_N, TANKTYPE_PG } from "src/const/TankTypes";
import { utils } from "react-grid-layout";


type DateState = {
    date: string
}

type DateAction = {
    isDateToday: () => boolean
    setDate: (date: Dayjs) => void
    getDayJSDate: () => Dayjs
    getISODate: () => string
    getDateForGQL: () => string
}


export const useDateStore = create<DateState & DateAction>(
    querystring(
        (set, get) => ({
            date: null,
            getDayJSDate: () => get().date ? dayjs(get().date, 'DD-MM-YYYY').set("hour", 9) : dayjs(),
            isDateToday: () => get().date ? dayjs(get().date).isSame(dayjs(), 'day') : true,
            setDate: (date) => {
                //If we get back to the current day
                if (date.isSame(dayjs(), "day"))
                    //Set the current time to now
                    set({ date : null})
                else
                    set({ date : date.format("DD-MM-YYYY")})
            },
            getISODate: () => {
                const date = get().date ? dayjs(get().date, 'DD-MM-YYYY').set("hours", 9) : dayjs();
                return date.toISOString()
            },
            getDateForGQL: () => {
                const date = get().date ? dayjs(get().date, 'DD-MM-YYYY').set("hours", 9) : dayjs();
                return date.format("YYYY-MM-DD")
            },
        }),
        {
            select(pathname) {
                return {
                    date: true
                }
            },
            // key: "q"
        }
    )
)

export type StateState = {
    site: TankInfosQuery["tank"]["site"],
    tank: TankInfosQuery["tank"],
    batch: XOR<TankInfosQuery["tank"]["lastBatchTransfer"]["batch"], BatchInfosQuery["batch"]>,
    stadePL: number,
    daysOfCulture: number,
    lastBatchTargets: RecursivePartial<Batch>[],
    batchTargetsPerBatch: {[key: number]: RecursivePartial<Batch>[]},
    lastLineTargets: number[],
    lineTargetsPerLine: {[key: number]: number[]},
    waterSrc: Partial<Water>,
    waterDest: Partial<Water>,
    reportingMode: string
    nextBatchTransfers: { [key: string]: RecursivePartial<BatchTransfer>[] }
    batchReportTarget?: RecursivePartial<BatchReportTarget>
}
type StateAction = {
    setSite: (site: Site) => void,
    setTank: (tank: Tank) => void,
    setBatch: (batch: Batch) => void,
    resetAllFromTank: () => void,
    setAllFromTank: (tank: TankInfosQuery["tank"], date: string) => void,
    setAllFromBatch: (batch: any, date: string) => void,
    setBatchTargets: (batchTargets: RecursivePartial<Batch>[], idBatch: number) => void,
    getBatchTargets: () => RecursivePartial<Batch>[],
    getBatchTargetsPerBatch: (idBatch: number) => RecursivePartial<Batch>[],
    getReviewsForResource: (resource: String) => (TankInfosQuery["tank"]["lastReview"]["comments"][0] & {review: TankInfosQuery["tank"]["lastReview"]})[],
    setWaterSrc: (water: Partial<Water>) => void,
    setWaterDest: (water: Partial<Water>) => void,
    setReportingMode: (reportingMode: string) => void,
    getNextBatchTransfersFlat: () => RecursivePartial<BatchTransfer>[],
    setNextBatchTransfers: (nextBatchTransfers: { [key: string]: RecursivePartial<BatchTransfer>[] }) => void,
    setLineTargets: (lineTargets: number[], idLine: number) => void,
    getLineTargets: () => number[],
    setBatchReportTarget: (batchReportTarget: RecursivePartial<BatchReportTarget>) => void,
}

export const useStateStore = create(
    persist<StateState & StateAction>((set, get) => ({
        site: null,
        tank: null,
        batch: null,
        stadePL: null,
        daysOfCulture: null,
        lastBatchTargets: null,
        batchTargetsPerBatch: {},
        lastLineTargets: null,
        lineTargetsPerLine: {},
        waterSrc: null,
        waterDest: null,
        reportingMode: null,
        nextBatchTransfers: null,
        batchReportTarget: null,
        resetAllFromTank: () => {
            set({ tank: null, batch:null, daysOfCulture: null, stadePL: null })
        },
        setAllFromTank: (tank, date) => {
            set({ tank: tank, batch: tank.lastBatchTransfer?.batch })
            const _date = date ? dayjs(date, "DD-MM-YYYY") : dayjs(new Date());

            const _daysOfCulture = _date.diff(dayjs(tank.lastBatchTransfer?.batch.batchInitial.createdAt), "days") +1
            set({ daysOfCulture: _daysOfCulture, stadePL: tank.lastBatchTransfer?.batch.batchInitial.stadePL + _daysOfCulture })
        },

        setAllFromBatch: (batch, date) => {

            const _date = date ? dayjs(date, "DD-MM-YYYY") : dayjs(new Date());

            const _daysOfCulture = batch ? _date.diff(dayjs(batch.createdAt), "days") +1 : 0
            set({ batch: batch, daysOfCulture: _daysOfCulture, stadePL: batch.batchInitial?.stadePL + _daysOfCulture, site: batch.site })
        },
        setSite: site => set({ site }),
        setTank: tank => set({ tank }),
        setBatch: batch => set({ batch }),
        setBatchTargets: (batchTargets, idBatch) => set({ batchTargetsPerBatch: {...get().batchTargetsPerBatch, [idBatch]: batchTargets}, lastBatchTargets: batchTargets}),
        getBatchTargets: () => {
            const batchTargets = get().batchTargetsPerBatch?.[get().batch?.id] || get().lastBatchTargets
            //Legacy patch to cast sessionStorage unique batch target to an array
            if(batchTargets && !Array.isArray(batchTargets))
                return [batchTargets]
            return batchTargets
        },
        getBatchTargetsPerBatch: (idBatch: number) => {
            const batchTargets = get().batchTargetsPerBatch?.[idBatch] || get().lastBatchTargets;
            //Legacy patch to cast sessionStorage unique batch target to an array
            if(batchTargets && !Array.isArray(batchTargets))
                return [batchTargets]
            return batchTargets
        },
        getReviewsForResource: (resource: string) => {
            const lastReview = get().tank?.lastReview;
            return lastReview?.comments?.reduce((acc, reviewComment) => {
                if(reviewComment.models.indexOf(resource) >= 0)
                    acc.push({
                        ...reviewComment,
                        review: lastReview
                    })
                return acc;
            }, [])
        },
        setWaterSrc: waterSrc => set({ waterSrc }),
        setWaterDest: waterDest => set({ waterDest }),
        setReportingMode: reportingMode => set({ reportingMode }),
        getNextBatchTransfersFlat: () =>  Object.values(get().nextBatchTransfers || {}).flat(),
        setNextBatchTransfers: nextBatchTransfers => set({ nextBatchTransfers }),
        setLineTargets: (lineTargets, idLine) => set({ lineTargetsPerLine: {...get().lineTargetsPerLine, [idLine]: lineTargets}, lastLineTargets: lineTargets}),
        getLineTargets: () => get().lineTargetsPerLine?.[get().tank?.line] || [],
        setBatchReportTarget: (batchReportTarget) => set({ batchReportTarget }),
        
    }), {
        name: 'state-storage', // name of the item in the storage (must be unique)
        storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
        //@ts-expect-error Persists only batchTarget
        partialize: (state) => ({ batchTargetsPerBatch: state.batchTargetsPerBatch, lineTargetsPerLine: state.lineTargetsPerLine, lastLineTargets: state.lastLineTargets, reportingMode: state.reportingMode }),
    })
)
type BatchTransferState = {
    batchTransfers: {[key: string]: RecursivePartial<BatchTransfer>}
    focusedBatchTransfer?: RecursivePartial<BatchTransfer>
}
type BatchTransferAction = {
    setBatchTransfers: (batchTransfers:{[key: string]: RecursivePartial<BatchTransfer>}) => void
    setBatchTransfersForTank: (batchTransfers: RecursivePartial<BatchTransfer>, idTank: number | string) => void
    setFocusedBatchTransfers: (batchTransfer: RecursivePartial<BatchTransfer>) => void
    resetBatchTransfersForTank: (idTank: number | string) => void
    resetBatchTransfers: () => void
}


export const useBatchTransferStore = create<BatchTransferState & BatchTransferAction>((set, get) => ({
    batchTransfers: {},
    setBatchTransfers: batchTransfers => set({ batchTransfers }),
    setBatchTransfersForTank: (batchTransfers, idTank) => set(state => ({ batchTransfers : {...state.batchTransfers, [idTank] : batchTransfers }})),
    setFocusedBatchTransfers: (batchTransfer) => set(state => ({ focusedBatchTransfer : batchTransfer})),
    resetBatchTransfersForTank: (idTank) => set(state => {
        let _batchTransfers = state.batchTransfers
        delete _batchTransfers[idTank]
        return ({ batchTransfers : _batchTransfers})
    }
    ),
    resetBatchTransfers: () => set({batchTransfers: {}})
})
)


type ConfigurationState = {
    tresholds: {[key: string]: RecursivePartial<Treshold>}
    batchReportTargets: RecursivePartial<BatchReportTarget>[]
    roleOverride: {
        selectedRoles: any[]
    }
}
type ConfigurationAction = {
    setTresholds: (tresholds:RecursivePartial<Treshold>) => void
    resetTresholds: () => void

    setBatchReportTargets: (batchReportTargets:RecursivePartial<BatchReportTarget>[]) => void
    resetBatchReportTargets: () => void

    setRoleOverride: (selectedRoles: any[]) => void
    resetRoleOverride: () => void
}


export const useConfigurationStore = create<ConfigurationState & ConfigurationAction>((set, get) => ({
    tresholds: {},
    setTresholds: tresholds => set({ tresholds: indexBy(tresholds, "unit") }),
    resetTresholds: () => set({tresholds: {}}),
    
    batchReportTargets: [],
    setBatchReportTargets: batchReportTargets => set({ batchReportTargets }),
    resetBatchReportTargets: () => set({batchReportTargets: []}),

    roleOverride: {
        selectedRoles: []
    },
    setRoleOverride: (selectedRoles: any[]) => set({ 
        roleOverride: { selectedRoles } 
    }),
    resetRoleOverride: () => set({ 
        roleOverride: { selectedRoles: [] } 
    })
})
)

type UserPreferencesState = {
    dataGridStates: {[idDataGrid: string]: GridInitialStatePro}
    dashboardStates: {
        [idPage: string]: {
            [idDashboard: string]: {
                layout: utils.LayoutItem, 
                graphs: string[],
                viewMode?: { [field: string]: string } | string,
                secondDimensions?: { [field: string]: string[] | null } | string,
                displayLegend?: { [field: string]: boolean },
                displayTarget?: { [field: string]: boolean },
                customNames?: { [field: string]: string },
                stacked?: { [field: string]: boolean },
                markerVisibility?: { [field: string]: { settling: boolean, event: boolean, batchTransfer: boolean } }
            }
        }
    }
    currentSite: string
    positiveMode: boolean
}
type UserPreferencesAction = {
    getDataGridState: (idDataGrid: string) => GridInitialStatePro
    setDataGridState: (idDataGrid: string, dataGridState:GridInitialStatePro) => void
    resetDataGridState: (idDataGrid: string) => void
    resetAllDataGridStates: () => void

    getDashboardStateOfPages: (idPage: string) => UserPreferencesState["dashboardStates"][0]
    getDashboardState: (idPage: string, idDashboard: string) => utils.LayoutItem
    renameDashboardState: (idPage: string, idDashboardOld: string, idDashboardNew: string) => void
    setDashboardState: (idPage: string, idDashboard: string, dashboardState:utils.LayoutItem) => void
    deleteDashboardState: (idPage: string, idDashboard: string) => void
    resetAllDashboardStates: () => void
    
    getMarkerVisibility: (idPage: string, idDashboard: string, field: string) => { settling: boolean, event: boolean, batchTransfer: boolean }
    setMarkerVisibility: (idPage: string, idDashboard: string, field: string, visibility: { settling: boolean, event: boolean, batchTransfer: boolean }) => void

    setCurrentSite: (string) => void,
    resetCurrentSite: () => void,
    
    setPositiveMode: (boolean) => void,
}

const DEFAULT_DASHBOARD_STATES: UserPreferencesState["dashboardStates"] = {
    "ReportProduction": {
        "COMOP": {
            graphs: [
                "biomass",
                "densitySurface",
                "fcr",
                "growth",
                "survival",
            ],
            layout: {
                lg: [
                    { i: "biomass", x: 0, y: 0, w: 6, h: 2, minW: 3, maxW: 12 },
                    { i: "densitySurface", x: 6, y: 0, w: 6, h: 2, minW: 3, maxW: 12 },
                    { i: "fcr", x: 0, y: 2, w: 4, h: 2, minW: 3, maxW: 12 },
                    { i: "growth", x: 4, y: 2, w: 4, h: 2, minW: 3, maxW: 12 },
                    { i: "survival", x: 8, y: 2, w: 4, h: 2, minW: 3, maxW: 12 },
                ]
            },
            viewMode: {},
            secondDimensions: {},
            displayLegend: {},
            displayTarget: {}, // Initialize empty object for target display state
            customNames: {}
        },
    },
    "BatchMonitoring": {
        "DEFAUT": {
            graphs: [
                "avgWeight",
                "Feeding_foodQuantity_sum",
                "ObservationalAnalysis_necrosis",
                "WaterAnalysis_mes",
                "PhysicochemicalAnalysis_tss",
                "WaterAnalysis_temperature",
                "WaterAnalysis_oxygen",
                "PhysicochemicalAnalysis_nitrite",
                "PhysicochemicalAnalysis_nitrate",
                "ObservationalAnalysis_digestiveTracts",
                "ObservationalAnalysis_shrimpsDead"
            ],
            layout: {
                "lg": [
                    { "w": 4, "h": 2, "x": 0, "y": 0, "i": "avgWeight", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 4, "y": 0, "i": "Feeding_foodQuantity_sum", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 8, "y": 0, "i": "ObservationalAnalysis_necrosis", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 0, "y": 2, "i": "WaterAnalysis_mes", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 2, "i": "PhysicochemicalAnalysis_tss", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 0, "y": 4, "i": "WaterAnalysis_temperature", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 3, "y": 4, "i": "WaterAnalysis_oxygen", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 6, "y": 4, "i": "PhysicochemicalAnalysis_nitrite", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 9, "y": 4, "i": "PhysicochemicalAnalysis_nitrate", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 0, "y": 6, "i": "ObservationalAnalysis_digestiveTracts", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 6, "i": "ObservationalAnalysis_shrimpsDead", "minW": 3, "maxW": 12, "moved": false, "static": false }
                ]
            }
        }
    },
    "Forecast": {
        "DEFAUT": {
            graphs: [
            ],
            layout: {
                "lg": [
                    { "w": 8, "h": 2, "x": 0, "y": 0, "i": "avgWeight_static", "minW": 4, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 8, "y": 0, "i": "foodQuantity_static", "minW": 3, "maxW": 12, "moved": false, "static": false },
                ]
            }
        }
    },
    "ForecastTank": {
        "DEFAUT": {
            graphs: [
                "WaterAnalysis_mes",
                "PhysicochemicalAnalysis_tss"
            ],
            layout: {
                "lg": [
                    { "w": 4, "h": 2, "x": 4, "y": 3, "i": "WaterAnalysis_mes", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 8, "y": 3, "i": "PhysicochemicalAnalysis_tss", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 3, "x": 0, "y": 0, "i": "batchCard", "minW": 2, "maxW": 12, "moved": false, "static": false },
                    { "w": 9, "h": 3, "x": 3, "y": 0, "i": "avgWeight_static", "minW": 4, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 0, "y": 3, "i": "foodQuantity_static", "minW": 3, "maxW": 12, "moved": false, "static": false }
                ],
            }
        }
    },
    "ReportProductionAggregatedYear" : {
        "COMOP":{
            "layout": {
                "lg": [
                    { "w": 3, "h": 2, "x": 3, "y": 0, "i": "fcr_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 6, "y": 0, "i": "growth_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 9, "y": 0, "i": "yearlyProduction_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 0, "y": 2, "i": "survival_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 6, "y": 2, "i": "densitySurface_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 0, "y": 4, "i": "plsOnBiomass_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 3, "y": 2, "i": "biomass_avg", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 0, "y": 0, "i": "Non marquée_biomass_sum", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 9, "y": 2, "i": "docN_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }
                ]
            },
            "graphs": [ "fcr_avg", "growth_avg", "yearlyProduction_avg", "survival_avg", "densitySurface_avg", "plsOnBiomass_avg", "biomass_avg", "Non marquée_biomass_sum", "docN_avg" ],
            "displayLegend": {
              "biomass_sum": false,
              "Non marquée_biomass_sum": true,
              "docN_avg": true,
              "biomass_avg": false,
              "survival_avg": true
            },
            "displayTarget": {
              "biomass_sum": true,
              "fcr_avg": true,
              "docN_avg": false
            },
            "viewMode": {
              "biomass_sum": "bar",
              "Non marquée_biomass_sum": "bar",
              "fcr_avg": "bar",
              "growth_avg": "bar",
              "yearlyProduction_avg": "bar",
              "plsOnBiomass_avg": "bar",
              "densitySurface_avg": "bar",
              "survival_avg": "bar",
              "biomass_avg": "bar",
              "docPG_avg": "bar",
              "docN_avg": "bar"
            },
            "secondDimensions": {
              "Non marquée_biomass_sum": [
                "Mue_biomass_sum",
                "Marquée_biomass_sum",
                "Sortie diverse_biomass_sum",
                "Morte_biomass_sum"
              ],
              "docN_avg": [
                "docPG_avg",
                "docG_avg"
              ],
              "biomass_avg": null,
              "survival_avg": null
            },
            "customNames": {
              "Non marquée_biomass_sum": "Biomasses",
              "fcr_avg": "FCR",
              "growth_avg": "Croissance",
              "docN_avg": "Evolution des jours d'élevage",
              "plsOnBiomass_avg": "Nb PL / biomasse",
              "biomass_avg": "Biomasse moyenne / récolte",
              "yearlyProduction_avg": "Indicateur de productivité",
              "survival_avg": "Survie",
              "densitySurface_avg": "Densité surfacique"
            },
            "stacked": {
                "Non marquée_biomass_sum": true
            }
        }
    },
    "ReportProductionAggregatedMonth" : {
        "COMOP":{
            "layout": {
                "lg": [
                    { "w": 3, "h": 2, "x": 3, "y": 3, "i": "fcr_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 3, "h": 2, "x": 6, "y": 3, "i": "growth_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 3, "h": 2, "x": 9, "y": 3, "i": "yearlyProduction_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 3, "h": 2, "x": 0, "y": 3, "i": "survival_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 4, "h": 2, "x": 8, "y": 7, "i": "densitySurface_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 4, "h": 2, "x": 0, "y": 7, "i": "plsOnBiomass_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 4, "h": 2, "x": 4, "y": 7, "i": "biomass_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 12, "h": 3, "x": 0, "y": 0, "i": "Non marquée_biomass_sum", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 6, "h": 2, "x": 6, "y": 5, "i": "docN_avg", "minW": 3, "maxW": 12, "moved": false, "static": false }, 
                    { "w": 6, "h": 2, "x": 0, "y": 5, "i": "calibersGraph", "moved": false, "static": false } 
                ]
            }, 
            "graphs": [
                "fcr_avg", "growth_avg", "yearlyProduction_avg", "survival_avg", "densitySurface_avg", "plsOnBiomass_avg", "biomass_avg", "Non marquée_biomass_sum", "docN_avg"
            ], 
            "displayLegend": {
                "biomass_sum": false, "Non marquée_biomass_sum": true, "docN_avg": true, "biomass_avg": false, "survival_avg": true }, 
            "displayTarget": {
                "biomass_sum": true, "fcr_avg": true, "docN_avg": false, "Non marquée_biomass_sum": false 
            }, 
            "viewMode": { 
                "biomass_sum": "bar", "Non marquée_biomass_sum": "bar", "fcr_avg": "line", "growth_avg": "line", "yearlyProduction_avg": "line", "plsOnBiomass_avg": "line", "densitySurface_avg": "line", "survival_avg": "line", "biomass_avg": "line", "docPG_avg": "bar", "docN_avg": "bar" 
            }, 
            "secondDimensions": { 
                "Non marquée_biomass_sum": [ "Mue_biomass_sum", "Marquée_biomass_sum", "Sortie diverse_biomass_sum", "Morte_biomass_sum" ], 
                "docN_avg": [ "docPG_avg", "docG_avg" ], 
                "biomass_avg": null, "survival_avg": null
            }, 
            "customNames": { 
                "Non marquée_biomass_sum": "Biomasses", "fcr_avg": "FCR", "growth_avg": "Croissance", "docN_avg": "Evolution des jours d'élevage", "plsOnBiomass_avg": "Nb PL / biomasse", "biomass_avg": "Biomasse moyenne / récolte", "yearlyProduction_avg": "Indicateur de productivité", "survival_avg": "Survie", "densitySurface_avg": "Densité surfacique"
            }, 
            "stacked": {
                "Non marquée_biomass_sum": true
            }
        }
    },
    "MonitoringTank": {
        "DEFAUT": {
            "layout": {
                "lg": [
                    { "w": 6, "h": 2, "x": 0, "y": 0, "i": "WaterAnalysis_mes", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 0, "i": "PhysicochemicalAnalysis_tss", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 0, "y": 2, "i": "WaterBalance_bicarbonate", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 3, "y": 2, "i": "PhysicochemicalAnalysis_nitrite", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 6, "y": 2, "i": "Feeding_foodQuantity", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 3, "h": 2, "x": 9, "y": 2, "i": "foodQuantity_water", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 0, "y": 4, "i": "WaterAnalysis_oxygen", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 4, "i": "WaterAnalysis_temperature", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 0, "y": 6, "i": "PhysicochemicalAnalysis_alkalinity", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 4, "y": 6, "i": "PhysicochemicalAnalysis_ph", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 4, "h": 2, "x": 8, "y": 6, "i": "Feeding_adjustmentCoef", "minW": 3, "maxW": 12, "moved": false, "static": false }
                ]
            },
            "graphs": [
                "WaterAnalysis_mes",
                "PhysicochemicalAnalysis_tss",
                "WaterBalance_bicarbonate",
                "PhysicochemicalAnalysis_nitrite",
                "Feeding_foodQuantity",
                "foodQuantity_water",
                "WaterAnalysis_oxygen",
                "WaterAnalysis_temperature",
                "PhysicochemicalAnalysis_alkalinity",
                "PhysicochemicalAnalysis_ph",
                "Feeding_adjustmentCoef"
            ]
        }
    },
    "Review": {
        "DEFAUT": {
            "layout": {
                "lg": [
                    { "w": 6, "h": 2, "x": 0, "y": 2, "i": "ObservationalAnalysis_necrosis", "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 0, "y": 4, "i": "Knackery_shrimpsDeadCount", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 4, "i": "ObservationalAnalysis_shrimpsDead", "minW": 4, "maxW": 12, "moved": false, "static": false },
                    { "w": 6, "h": 2, "x": 6, "y": 2, "i": "WaterAnalysis_mes", "minW": 4, "maxW": 12, "moved": false, "static": false }
                ],
                "md": [
                    { "w": 5, "h": 2, "x": 0, "y": 2, "i": "ObservationalAnalysis_necrosis", "moved": false, "static": false },
                    { "w": 5, "h": 2, "x": 0, "y": 4, "i": "Knackery_shrimpsDeadCount", "minW": 3, "maxW": 12, "moved": false, "static": false },
                    { "w": 5, "h": 2, "x": 5, "y": 4, "i": "ObservationalAnalysis_shrimpsDead", "minW": 4, "maxW": 12, "moved": false, "static": false },
                    { "w": 5, "h": 2, "x": 5, "y": 2, "i": "WaterAnalysis_mes", "minW": 4, "maxW": 12, "moved": false, "static": false }
                ]
            },
            "graphs": [
                "ObservationalAnalysis_necrosis",
                "Knackery_shrimpsDeadCount",
                "ObservationalAnalysis_shrimpsDead",
                "WaterAnalysis_mes"
            ]
        }
    }
}
export const useUserPreferencesStore = create(
    persist<UserPreferencesState & UserPreferencesAction>((set, get) => ({
        dataGridStates: {},
        getDataGridState: (idDataGrid: string) => get().dataGridStates[idDataGrid],
        setDataGridState: (idDataGrid: string, dataGridState: GridInitialStatePro) => set(state => ({ dataGridStates: { ...state.dataGridStates, [idDataGrid]: dataGridState } })),
        resetDataGridState: (idDataGrid: string) => set(state => {
            let _dataGridStates = state.dataGridStates
            delete _dataGridStates[idDataGrid]
            return ({ dataGridStates: _dataGridStates })
        }
        ),
        resetAllDataGridStates: () => set({ dataGridStates: {} }),


        dashboardStates: DEFAULT_DASHBOARD_STATES,
        getDashboardStateOfPages: (idPage: string) => get().dashboardStates[idPage] || DEFAULT_DASHBOARD_STATES[idPage] || {},
        getDashboardState: (idPage: string, idDashboard: string) => get().dashboardStates[idPage]?.[idDashboard],
        //https://stackoverflow.com/a/50101979/1437016
        renameDashboardState: (idPage: string, idDashboardOld: string, idDashboardNew: string) =>  set(state => {
            let _dashboardStates = {}
            delete Object.assign(_dashboardStates, state.dashboardStates[idPage], {[idDashboardNew]: state.dashboardStates[idDashboardOld] })[idDashboardOld];
            return ({ dashboardStates: {...state.dashboardStates, [idPage] : _dashboardStates} })
        }
        ),
        setDashboardState: (idPage: string, idDashboard: string, dashboardState: utils.LayoutItem) => set(state => ({ dashboardStates: { ...state.dashboardStates, [idPage]: { ...state.dashboardStates[idPage], [idDashboard]: dashboardState }} })),
        deleteDashboardState: (idPage: string, idDashboard: string) => set(state => {
            let _dashboardStates = state.dashboardStates
            delete _dashboardStates[idPage]?.[idDashboard]
            return ({ dashboardStates: _dashboardStates })
        }
        ),
        resetAllDashboardStates: () => set({ dashboardStates: DEFAULT_DASHBOARD_STATES }),
        
        getMarkerVisibility: (idPage: string, idDashboard: string, field: string) => {
            const defaultVisibility = { settling: false, event: true, batchTransfer: true };
            return get().dashboardStates[idPage]?.[idDashboard]?.markerVisibility?.[field] || defaultVisibility;
        },
        setMarkerVisibility: (idPage: string, idDashboard: string, field: string, visibility: { settling: boolean, event: boolean, batchTransfer: boolean }) => set(state => ({
            dashboardStates: {
                ...state.dashboardStates,
                [idPage]: {
                    ...state.dashboardStates[idPage],
                    [idDashboard]: {
                        ...state.dashboardStates[idPage]?.[idDashboard],
                        markerVisibility: {
                            ...state.dashboardStates[idPage]?.[idDashboard]?.markerVisibility,
                            [field]: visibility
                        }
                    }
                }
            }
        })),

        currentSite: null,
        setCurrentSite: currentSite => set({ currentSite }),
        resetCurrentSite: () => set({currentSite: null}),

        positiveMode: null,
        setPositiveMode: positiveMode => set({ positiveMode }),
    }), {
        name: 'user-preferences-storage', // name of the item in the storage (must be unique)
        storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
        // partialize: (state) => ({ batchTargetsPerBatch: state.batchTargetsPerBatch }),
    })
)


export const BiomassReportConfTypes = {
    avgWeightConf: {type: ["ESTIMATED", "FORECAST", "REAL"] as const},
    biomassConf: {type: ["ESTIMATED", "FORECAST", "REAL", "PACKAGINGS"] as const},
    fcrConf: {type: ["FORECAST", "REAL"] as const},
    survivalConf: {type: ["FORECAST", "REAL", "POST_MORTALITY_N"] as const},
    growthConf: {type: ["FORECAST", "REAL"] as const},
    densityConf: {type: ["REAL"] as const},
    yearlyProductionConf: {type: ["REAL"] as const},
    plsOnBiomassConf: {type :[ "REAL", "POST_MORTALITY_N"] as const},
}
//https://stackoverflow.com/a/64174790/1437016
type BiomassReportState = {
    avgWeightConf: {type: typeof BiomassReportConfTypes.avgWeight.type[number]},
    biomassConf: {type: typeof BiomassReportConfTypes.biomassConf.type[number]},
    fcrConf: {type: typeof BiomassReportConfTypes.fcrConf.type[number]},
    survivalConf: {type: typeof BiomassReportConfTypes.survivalConf.type[number]},
    growthConf: {type: typeof BiomassReportConfTypes.growthConf.type[number]},
    densityConf: {type: typeof BiomassReportConfTypes.densityConf.type[number]},
    yearlyProductionConf: {type: typeof BiomassReportConfTypes.yearlyProductionConf.type[number]},
    plsOnBiomassConf: {type: typeof BiomassReportConfTypes.plsOnBiomassConf.type[number]},
}
type BiomassReportAction = {
    setBiomassConf: (conf: BiomassReportState["biomassConf"]) => void,
    setFcrConf: (conf: BiomassReportState["fcrConf"]) => void,
    setSurvivalConf: (conf: BiomassReportState["survivalConf"]) => void,
    setGrowthConf: (conf: BiomassReportState["growthConf"]) => void,
    setDensityConf: (conf: BiomassReportState["densityConf"]) => void,
    setYearlyProductionConf: (conf: BiomassReportState["yearlyProductionConf"]) => void,
    setPlsOnBiomassConf: (conf: BiomassReportState["plsOnBiomassConf"]) => void,

    getBiomass: (batch:FindBatchReportQuery["batch"], type?: string) => number,
    getAvgWeight: (batch:FindBatchReportQuery["batch"]) => number,
    getFcr: (batch:FindBatchReportQuery["batch"]) => number,
    getSurvival: (batch:FindBatchReportQuery["batch"], type?: string) => number,
    getGrowth: (batch:FindBatchReportQuery["batch"]) => number,
    getDensity: (batch:FindBatchReportQuery["batch"]) => number,
    getYearlyProduction: (batch:FindBatchReportQuery["batch"]) => number,
    getPlsOnBiomass: (batch:FindBatchReportQuery["batch"]) => number,
    computeKPI: (batch:FindBatchReportQuery["batch"]) => {[key:string]:number},
    computeBatchReport: (batch:FindBatchReportQuery["batch"]) => Partial<BatchReport>,
}


export const useBiomassReportStore = create(
    persist<BiomassReportState & BiomassReportAction>((set, get) => ({
        avgWeightConf: {type: "REAL"},
        biomassConf: {type: "REAL"},
        fcrConf: {type: "REAL"},
        survivalConf: {type: "REAL"},
        growthConf: {type: "REAL"},
        densityConf: {type: "REAL"},
        yearlyProductionConf: {type: "REAL"},
        plsOnBiomassConf: {type: "REAL"},
        setBiomassConf: (conf: BiomassReportState["biomassConf"]) => set(state => ({...state, biomassConf: conf})),
        setFcrConf: (conf: BiomassReportState["fcrConf"]) => set(state => ({...state, fcrConf: conf})),
        setSurvivalConf: (conf: BiomassReportState["survivalConf"]) => set(state => ({...state, survivalConf: conf})),
        setGrowthConf: (conf: BiomassReportState["growthConf"]) => set(state => ({...state, growthConf: conf})),
        setDensityConf: (conf: BiomassReportState["densityConf"]) => set(state => ({...state, densityConf: conf})),
        setYearlyProductionConf: (conf: BiomassReportState["yearlyProductionConf"]) => set(state => ({...state, yearlyProductionConf: conf})),
        setPlsOnBiomassConf: (conf: BiomassReportState["plsOnBiomassConf"]) => set(state => ({...state, plsOnBiomassConf: conf})),
        /**
         * @deprecated type ESTIMATED is deprecated biomass_estimated isn't computed anymore
         */
        getBiomass: (batch:FindBatchReportQuery["batch"], type = null) => {
            if(!batch.harvest.report)
                return null;
            switch(type || get().biomassConf.type) {
                case "ESTIMATED":
                    return (batch.lastBatchProduction.biomass_estimated / 1000)
                case "FORECAST":
                    return (batch.lastBatchProduction.biomass / 1000)
                case "REAL":
                    return batch.harvest.report.biomass / 1000
                    //EDIT : removed calculation that was done here, it's done at harvest report step now
                    // if(!batch.harvest.shrimpWeighingsCount)
                    //     return (batch.harvest.report?.biomass_sellable / 1000)
                    // //Sum weighings and all sellable sort types that are not weighed
                    // const sortTypesWeighingsIds: number[] = batch.harvest.shrimpWeighingsDistinctSortTypes.map(shrimpWeighing => shrimpWeighing.sortType.id);
                    // const sortTypesWeighings = batch.harvest.report?.detailsBySortType.filter(({sortType}) => !sortTypesWeighingsIds.includes(sortType.id) && sortType.sellable)?.reduce((acc, detail) => acc += detail.biomass, 0);
                    // const offMarketSorts = batch.harvest.offMarketSorts.reduce((acc, sort) => acc += sort.weight, 0);
                    // //Add off market sort too
                    // return ((batch.harvest.shrimpWeighingsSum + sortTypesWeighings + offMarketSorts) / 1000)
                case "PACKAGINGS":
                    return (batch.harvest.report?.biomass_sellable / 1000)
            }
        },
        getAvgWeight: (batch:FindBatchReportQuery["batch"]) => {
            if(batch.harvest.report)
                return batch.harvest.report?.avgWeight
            else 
                return batch.lastBatchProduction.avgWeight
        },
        getFcr: (batch:FindBatchReportQuery["batch"]) => {
            if(get().fcrConf.type == "FORECAST")
                return batch.lastBatchProduction.fcr_total
            else{
                const fcr = (batch.lastBatchProduction.foodQuantity_sum / (get().getBiomass(batch)*1000))
                return isFinite(fcr) ? fcr : 0
            } 
        },
        /**
         * @deprecated type ESTIMATED is deprecated quantity_estimated isn't computed anymore
         */
        getSurvival: (batch:FindBatchReportQuery["batch"], type = null) => {
            const _quantity = get().getBiomass(batch, type) / get().getAvgWeight(batch) * 1000
            switch(type || get().survivalConf.type) {
                case "ESTIMATED":
                    return (batch.lastBatchProduction.quantity_estimated / batch.quantity)
                case "FORECAST":
                    return (batch.lastBatchProduction.quantity / batch.quantity)
                case "REAL":
                    return (_quantity / batch.quantity)
                case "POST_MORTALITY_N":
                    return (_quantity / (batch.quantity - (batch.quantity * batch.batchInitial.provider.mortalityTransferN)))
            }
        },
        getGrowth: (batch:FindBatchReportQuery["batch"]) => (
            get().growthConf.type == "FORECAST" 
            ? (batch.lastBatchProduction.avgWeight / batch.lastBatchProduction.daysOfCulture)
            : (get().getAvgWeight(batch) / batch.lastBatchProduction.daysOfCulture)
        ),
        getDensity: (batch:FindBatchReportQuery["batch"]) => (
            get().getBiomass(batch) / (batch.lastBatchTransfer.tankSrc.width * batch.lastBatchTransfer.tankSrc.length)
        ),
        getYearlyProduction: (batch:FindBatchReportQuery["batch"]) => (
            (get().getBiomass(batch)
            / batch.lastBatchProduction.daysOfCulture)
            * 18
            * 365
            / 1000
        ),
        getPlsOnBiomass: (batch:FindBatchReportQuery["batch"]) => {
            let quantity;
            if(get().plsOnBiomassConf.type == "REAL")
                quantity = batch.quantity
            else 
                quantity = (batch.quantity - (batch.quantity * batch.batchInitial.provider.mortalityTransferN))

            const plsOnBiomass = quantity / get().getBiomass(batch)
            return isFinite(plsOnBiomass) ? plsOnBiomass : 0
        },
        computeKPI: (batch:FindBatchReportQuery["batch"]) => ({
            biomass: get().getBiomass(batch),
            fcr: get().getFcr(batch),
            survival: get().getSurvival(batch),
            growth: get().getGrowth(batch),
            density: get().getDensity(batch),
            yearlyProduction: get().getYearlyProduction(batch),
            plsOnBiomass: get().getPlsOnBiomass(batch)
        }),

        computeBatchReport: (batch:FindBatchReportQuery["batch"]) => {
            // if(!batch.harvest.report?.)
            //     return {}

            const lastProductionN = batch.productions.findLast(day => day.TankType_name == TANKTYPE_N),
                  lastProductionPG = batch.productions.findLast(day => day.TankType_name == TANKTYPE_PG),
                  lastProductionG = batch.productions.findLast(day => day.TankType_name == TANKTYPE_G),
                  firstSettling = batch.productions.find(day => !!day.Settling_mud),
                  lastSettling = batch.productions.findLast(day => !!day.Settling_mud);
            
            return {
                idBatch: batch.id,
                biomass: (get().getBiomass(batch) * 1000) + (batch.subBatches?.reduce((acc, subBatch) => acc += (subBatch.isPartialHarvest ? subBatch.biomass : 0), 0)  || 0), //Don't forget to convert kg to g again. Sum all subBatches weight if present
                fcr: get().getFcr(batch),
                survival: get().getSurvival(batch),
                growth: get().getGrowth(batch),
                yearlyProduction: get().getYearlyProduction(batch) * 1000, // Convert t to kg
                plsOnBiomass: get().getPlsOnBiomass(batch),
                dateBatchTransferN: batch.batchTransfers.find(transfer => transfer.tankDest?.tank_type.name == TANKTYPE_N)?.createdAt,
                dateBatchTransferNPG: batch.batchTransfers.find(transfer => transfer.tankDest?.tank_type.name == TANKTYPE_PG)?.createdAt,
                dateBatchTransferPGG: batch.batchTransfers.find(transfer => transfer.tankDest?.tank_type.name == TANKTYPE_G)?.createdAt,
                dateBatchTransferGA: batch.batchTransfers.find(transfer => !transfer.tankDest)?.createdAt,
                doc: batch.lastBatchProduction.daysOfCulture,
                docN: lastProductionN?.daysOfCultureInTank,
                docPG: lastProductionPG?.daysOfCultureInTank,
                docG: lastProductionG?.daysOfCultureInTank,
                doc1g: batch.productions.find(day => day.avgWeight > 1)?.daysOfCulture,
                doc5g: batch.productions.find(day => day.avgWeight > 5)?.daysOfCulture,
                doc10g: batch.productions.find(day => day.avgWeight > 10)?.daysOfCulture,
                doc15g: batch.productions.find(day => day.avgWeight > 15)?.daysOfCulture,
                doc20g: batch.productions.find(day => day.avgWeight > 20)?.daysOfCulture,
                doc25g: batch.productions.find(day => day.avgWeight > 25)?.daysOfCulture,
                avgWeight: get().getAvgWeight(batch),
                avgWeightType: batch.harvest.report?.avgWeightType || "ESTIMATED",
                densitySurface: get().getBiomass(batch) / (batch.lastBatchTransfer.tankSrc.width * batch.lastBatchTransfer.tankSrc.length),
                densityVolume: get().getBiomass(batch) / (batch.lastBatchTransfer.tankSrc.width * batch.lastBatchTransfer.tankSrc.length * batch.lastBatchTransfer.tankSrc.waterHeight),
                quantityFinal: Math.round(get().getBiomass(batch) * 1000 / get().getAvgWeight(batch)), //convert back biomass to g
                quantityInitialPostMortalityN: Math.round(batch.quantity - (batch.quantity * batch.batchInitial.provider.mortalityTransferN)),
                quantityInitial: batch.quantity,
                foodQuantity: batch.lastBatchProduction.foodQuantity_sum,
                foodQuantityN: (lastProductionN?.foodQuantity_sum || 0),
                foodQuantityPG: lastProductionPG ? (lastProductionPG?.foodQuantity_sum || 0) - (lastProductionN?.foodQuantity_sum || 0) : 0,
                foodQuantityG: lastProductionG ? (lastProductionG?.foodQuantity_sum || 0) - (lastProductionPG?.foodQuantity_sum || 0) : 0,
                settlingsMudSum: batch.lastBatchProduction.mud_sum,
                settlingsCount: batch.settlingsCount,
                settlingsFrequency: (lastSettling && lastSettling != firstSettling) ? (batch.settlingsCount / (lastSettling.daysOfCulture - firstSettling.daysOfCulture)) : 0, //Cover cases when there's 0 or 1 settling, to avoid Inifinity result
                settlingsTimeAvg: batch.settlingsTimeAvg || 0,
                eventsCount: batch.eventsCount,
                eventsHighCount: batch.eventsHighCount,
                eventsMortalitySum: batch.eventsMortalitySum || 0, //explicit cast null to 0
                eventsBiomassSubtractSum: batch.eventsBiomassSubtractSum || 0, //explicit cast null to 0

            }
        },
        
    }), {
        name: 'biomass-report-storage', // name of the item in the storage (must be unique)
        storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
        // partialize: (state) => ({ batchTargetsPerBatch: state.batchTargetsPerBatch }),
    })
)